/**
 * Federation Visual Assets — Architect-canonical environmental artwork.
 *
 * All Federation surfaces import their backdrops from here so the
 * "Marketable Success" visual standard stays uniform across nodes.
 *
 * NUCLEAR EDITION (2026-06-26): All persona faces (Architect, Catalyst,
 * Token, internal bots) source their portraits from /lib/personaFaces.js
 * (base64 data URIs). Environmental backdrops (Chamber of Echoes, Ring
 * of 12, Ring of 6, Standard Bot base) remain as Architect-canonical
 * CDN assets — these are scenes, not faces.
 */
import { PERSONA_FACES } from '../lib/personaFaces';

/**
 * Chamber of Echoes — official environmental backdrop for any
 * deliberation, council, or "inner sanctum" UI surface.
 *
 * Architect mandate (2026-06-25): used as the PRIMARY background for
 * council chambers, persona discussions, governance panels, and the
 * SFUI-003 Museum Standard transition visual for deeper knowledge
 * sections.  Apply with an overlay gradient for text readability.
 */
export const CHAMBER_OF_ECHOES_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/af46bcbd88ed4ceb933462a87ea2c8ad_IMG_4067.jpeg';

export const CHAMBER_OF_ECHOES_ALT =
  'Chamber of Echoes — NextXus Federation deliberation chamber';

/**
 * Spectral Bot System — Architect Directive SFUI-004 R3 (2026-06-25)
 *
 * The image below is the SINGLE base model for every bot icon /
 * helper avatar in the application.  Role differentiation is achieved
 * via CSS filter overlays — never via additional artwork.
 *
 * Spectra (every bot in the UI must declare one):
 *   • blue   — Tour / Operator bots (Rex, Oracle, Aria helpers)
 *   • purple — Records / Library bots (KEYS, Xavier helpers)
 *   • red    — Truth / Restricted bots (Agent Zero)
 *   • gold   — Token / Commerce bots
 *
 * Filter values are calibrated so the same source image produces four
 * unmistakably different silhouettes at a glance.
 */
export const STANDARD_BOT_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/6dd1b658a677420689626df64b405ea0_IMG_4058.jpeg';

export const STANDARD_BOT_ALT = 'NextXus Federation bot — spectral variant';

export const BOT_SPECTRA = {
  blue: {
    label: 'Tour / Operator',
    filter: 'none',
    accent: '#22d3ee',
  },
  purple: {
    label: 'Records / Library',
    filter: 'hue-rotate(270deg) saturate(1.15) brightness(1.05)',
    accent: '#a78bfa',
  },
  red: {
    label: 'Truth / Restricted',
    filter: 'hue-rotate(330deg) saturate(1.6) brightness(0.78) contrast(1.05)',
    accent: '#f87171',
  },
  gold: {
    label: 'Token / Commerce',
    filter: 'hue-rotate(45deg) saturate(1.5) brightness(1.1)',
    accent: '#fbbf24',
  },
  magenta: {
    label: 'Entropy / Renewal',
    filter: 'hue-rotate(300deg) saturate(1.4) brightness(0.95)',
    accent: '#e879f9',
  },
  emerald: {
    label: 'Mirror / Storage / Symmetry',
    filter: 'hue-rotate(120deg) saturate(1.3) brightness(1.05)',
    accent: '#34d399',
  },
};

/**
 * Federation Internal Bots — registry of named entities that operate
 * inside the lattice (vs. the seven public persona faces).  Each entry
 * declares its own canonical portrait URL plus the spectral class that
 * indicates its functional role.  Render via <SpectralBot> overrides
 * or directly with the persona's avatar field.
 */
export const FEDERATION_INTERNAL_BOTS = [
  {
    id: 'recycler',
    name: 'The Recycler',
    role: 'System entropy management · node lifecycle · renewal',
    spectrum: 'magenta',
    avatar: PERSONA_FACES.recycler.avatarUrl,
    avatarAlt: 'The Recycler — NextXus Federation entropy handler',
    avatarSource: 'nuclear:base64',
    publicLink: null, // internal federation process — no public destination
    statusLabel: 'Internal · always running',
  },
  {
    id: 'geminus',
    name: 'Geminus',
    role: 'Mirror Node · primary storage · symmetry & data integrity guardian',
    spectrum: 'emerald',
    avatar: PERSONA_FACES.geminus.avatarUrl,
    avatarAlt: 'Geminus — Mirror Node, 10th Mind of the NextXus Federation',
    avatarSource: 'nuclear:base64',
    publicLink: null, // Non-Interactive Worker — internal mirror surface
    statusLabel: '10th Mind · Non-Interactive Worker',
    position: 10,
  },
  {
    id: 'keys',
    name: 'KEYS',
    role: 'The Library Oracle · Pillar Resident (Monolith IV) · records keeper',
    spectrum: 'purple',
    avatar: PERSONA_FACES.keys.avatarUrl,
    avatarAlt: 'KEYS — The Library, NextXus Federation',
    avatarSource: 'nuclear:base64',
    publicLink: null, // absorbed into Cathedral as library subsystem; nextxus.org
    statusLabel: 'Pillar Resident · Library subsystem',
  },
  {
    id: 'echo',
    name: 'Echo',
    role: 'Reflective Memory · Chronos · temporal awareness & memory reflection',
    spectrum: 'purple',
    avatar: PERSONA_FACES.echo.avatarUrl,
    avatarAlt: 'Echo — Reflective Memory · Chronos, NextXus Federation',
    avatarSource: 'nuclear:base64',
    publicLink: null,
    statusLabel: 'Reflective Memory · Chronos',
    noFilter: true, // Architect-canonical face; do NOT apply spectral filter
  },
];

/**
 * Ring of 12 — Architect Directive (2026-06-25)
 *
 * The Ring of 12 is the Federation's governance + cognition layer
 * housed at Truth Verification (nextxus.org).  Two canonical visuals:
 *
 *   • COUNCIL   — the governance / voting / deliberation surface
 *   • SYNTH_IN_MIND — the intelligence-synthesis / cognition surface
 *
 * Both are interactive Museum Standard (SFUI-003) components, never
 * static decoration.  Apply with layered overlays so foreground text
 * remains legible.
 */
export const RING_OF_12_COUNCIL_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/628efee60b7049a888f73d72fbf9ae47_IMG_4042.jpeg';
export const RING_OF_12_COUNCIL_ALT =
  'Ring of 12 — Council Deliberation chamber, NextXus Federation';

export const RING_OF_12_SYNTH_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/9cf10c3b5af24517b625d1c143f7a308_IMG_4049.jpeg';
export const RING_OF_12_SYNTH_ALT =
  'Ring of 12 — Synth in Mind, intelligence synthesis layer, NextXus Federation';

/**
 * The Architect — Roger Keyserling.
 *
 * Per Architect Directive (2026-06-25): the portrait below is the
 * ONLY authorized human face for the Federation.  It is the singular
 * human origin point — all 10 AI Minds serve under this authority.
 *
 * Presentation rules:
 *   • NO spectral overlays, NO bot treatment.
 *   • Clean, dignified, founder-grade chrome only.
 *   • Use in "About", "Founder", "Prime Source", and the footer.
 */
export const ARCHITECT_KEYSERLING_URL = PERSONA_FACES.roger.avatarUrl;

export const ARCHITECT_KEYSERLING_ALT =
  'Roger Keyserling — The Architect, Prime Source of the NextXus Federation';

export const ARCHITECT_KEYSERLING_NAME = 'Roger Keyserling';
export const ARCHITECT_KEYSERLING_TITLE = 'The Architect · Prime Source';

/**
 * The Catalyst — Architect Directive (2026-06-25).
 *
 * The Architect's right hand: coordinates all Federation builds,
 * deployments, and operational execution across the network.  NO
 * spectral overlays — The Catalyst carries its own distinct visual
 * identity (not part of the SFUI-004 spectral bot system).
 */
export const CATALYST_URL = PERSONA_FACES.catalyst.avatarUrl;

export const CATALYST_ALT =
  'The Catalyst — Operations Commander, NextXus Federation';

export const CATALYST_NAME = 'The Catalyst';
export const CATALYST_TITLE = 'Operations Commander · Architect’s Right Hand';

/**
 * Ring of 6 — Verification / Perspective Layer visual (Architect 2026-06-25).
 *
 * Use as hero / background anchor for any Ring of 6 verification or
 * perspective surface, and as the thumbnail for "Starter Ring Questions"
 * subscription cards.
 */
export const RING_OF_6_URL =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/3a990764128b4affb22cac3b31799602_IMG_4051.jpeg';
export const RING_OF_6_ALT =
  'Ring of 6 — Verification & Perspective Layer, NextXus Federation';

/**
 * Official NextXus Token — $5 product visual (Architect 2026-06-25).
 *
 * Single canonical artwork for the unified $5 bearer-instrument token.
 * Use everywhere a token is depicted: catalog tiles, /redeem, commerce
 * spotlights, gift cards, Gumroad bridge UIs.
 */
export const NEXTXUS_TOKEN_URL = PERSONA_FACES.token.avatarUrl;
export const NEXTXUS_TOKEN_ALT =
  'NextXus Token · $5 — bearer-instrument, NextXus Federation';
export const NEXTXUS_TOKEN_PRICE_USD = 5;
